import { Fingerprint, KeyRound, Loader2, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useAnchorChain } from '@/features/wallet/hooks/useAnchorChain';
import { useWallet } from '@/features/wallet/hooks/useWallet';
import { listPasskeys } from '@/lib/api/tradingSession';
import type { PasskeyCredential } from '@/lib/api/tradingSession';
import { describeErrorText } from '@/lib/errors';

import { detectPasskeyDeviceKind, suggestPasskeyLabel } from '../passkeyLabel';
import { usePasskeyManagementStore } from '../passkeyManagementStore';
import {
    completePasskeyRegistration,
    completePasskeyRemoval,
    type PasskeyRegistrationPhase,
} from '../passkeyRegistrationCeremony';
import { useTradingSessionStore } from '../store';

const MAX_PASSKEYS = 10;

/** Which prompt the user should be looking at, per registration step. */
const PHASE_BUTTON: Record<PasskeyRegistrationPhase, string> = {
    creating: 'Saving passkey…',
    possession: 'Confirm with passkey…',
    association: 'Sign in wallet…',
    mining: 'Waiting for Cronos…',
    confirming: 'Activating…',
};
const PHASE_DETAIL: Record<PasskeyRegistrationPhase, string> = {
    creating:
        'Step 1 of 3. Save the new passkey in the prompt your browser just opened.',
    possession:
        'Step 2 of 3. Use the passkey you just saved once, so it is proven to work.',
    association:
        'Step 3 of 3. Sign the Cronos transaction in your wallet. It registers the passkey on your account.',
    mining:
        'Waiting for the Cronos transaction to confirm. This usually takes a few seconds.',
    confirming: 'Transaction confirmed. Activating the passkey on your account.',
};

function statusLabel(credential: PasskeyCredential): string {
    if (credential.status === 'active') return 'Active on Cronos';
    if (credential.status === 'pending') return 'Pending Cronos activation';
    return 'Removed';
}

const PasskeyManagementDialog: React.FC = () => {
    const open = usePasskeyManagementStore((state) => state.isOpen);
    const close = usePasskeyManagementStore((state) => state.close);
    const reopenStore = usePasskeyManagementStore((state) => state.open);
    const reopenPending = usePasskeyManagementStore(
        (state) => state.reopenAfterWalletAuthority,
    );
    const markReopen = usePasskeyManagementStore(
        (state) => state.markReopenAfterWalletAuthority,
    );
    const clearReopen = usePasskeyManagementStore(
        (state) => state.clearReopenAfterWalletAuthority,
    );
    const reauthenticationOpen = useTradingSessionStore(
        (state) => state.reauthenticationOpen,
    );
    const status = useTradingSessionStore((state) => state.status);
    const clockNowMs = useTradingSessionStore((state) => state.clockNowMs);
    const sessionLoading = useTradingSessionStore((state) => state.isLoading);
    const requestConnectedWalletWindow = useTradingSessionStore(
        (state) => state.requestConnectedWalletWindow,
    );
    const { address, connect } = useWallet();
    const { anchorChainId } = useAnchorChain();
    const [credentials, setCredentials] = useState<PasskeyCredential[]>([]);
    const [label, setLabel] = useState('');
    // the label is prefilled ("Mac 1", "Mobile 2", …) from the device and the
    // keys already registered; anything the user typed themselves is kept.
    const [labelEdited, setLabelEdited] = useState(false);
    const suggestedLabel = suggestPasskeyLabel(
        detectPasskeyDeviceKind(),
        credentials.map((credential) => credential.label),
    );
    useEffect(() => {
        if (!labelEdited) setLabel(suggestedLabel);
    }, [labelEdited, suggestedLabel]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [phase, setPhase] = useState<PasskeyRegistrationPhase | null>(null);
    const [error, setError] = useState<string | null>(null);
    const hasOwnerAuthority =
        status?.state === 'active' &&
        status.method === 'eip712' &&
        useTradingSessionStore.getState().hasActiveAuthority(clockNowMs);

    const refresh = useCallback(async () => {
        setCredentials(await listPasskeys());
    }, []);

    useEffect(() => {
        if (!open) return;
        setError(null);
        if (!hasOwnerAuthority) {
            setCredentials([]);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        void refresh()
            .catch((caught) =>
                setError(
                    describeErrorText(caught, { operation: 'Passkey list' }),
                ),
            )
            .finally(() => setIsLoading(false));
    }, [hasOwnerAuthority, open, refresh]);

    const handleAdd = async () => {
        if (!hasOwnerAuthority) return;
        if (credentials.length >= MAX_PASSKEYS) return;
        if (!address) {
            try {
                await connect();
            } catch (caught) {
                setError(
                    describeErrorText(caught, {
                        operation: 'Wallet connection',
                    }),
                );
            }
            return;
        }

        setIsLoading(true);
        setError(null);
        try {
            await completePasskeyRegistration({
                owner: address,
                anchorChainId,
                ...(label.trim() ? { label: label.trim() } : {}),
                onPhase: setPhase,
            });
            setLabelEdited(false);
            await refresh();
        } catch (caught) {
            setError(
                describeErrorText(caught, { operation: 'Passkey update' }),
            );
        } finally {
            setPhase(null);
            setIsLoading(false);
        }
    };

    const handleRemove = async (credential: PasskeyCredential) => {
        if (!hasOwnerAuthority) return;
        if (!address) {
            try {
                await connect();
            } catch (caught) {
                setError(
                    describeErrorText(caught, {
                        operation: 'Wallet connection',
                    }),
                );
            }
            return;
        }

        setBusyId(credential.id);
        setError(null);
        try {
            await completePasskeyRemoval({
                owner: address,
                credential,
                anchorChainId,
            });
            await refresh();
        } catch (caught) {
            setError(
                describeErrorText(caught, { operation: 'Passkey update' }),
            );
        } finally {
            setBusyId(null);
        }
    };

    const openConnectedWalletWindow = useCallback(() => {
        // remember why the user is being routed through the wallet sign-in so
        // this dialog can come straight back once the authority lands.
        markReopen();
        close();
        requestConnectedWalletWindow({
            operation: 'Passkey management',
            reason: 'anchorTransaction',
        });
    }, [close, markReopen, requestConnectedWalletWindow]);

    // no owner authority: go straight to the wallet window instead of showing
    // a dead-end panel. a cancelled window leaves this dialog closed (the
    // reopen effect below only reopens on granted authority), so no loop.
    useEffect(() => {
        if (!open || sessionLoading || hasOwnerAuthority || reopenPending) {
            return;
        }
        openConnectedWalletWindow();
    }, [
        hasOwnerAuthority,
        open,
        openConnectedWalletWindow,
        reopenPending,
        sessionLoading,
    ]);

    // auto-reopen after the Connected Wallet window resolves: applyStatus runs
    // before the window closes, so on the falling edge of reauthenticationOpen
    // the eip712 authority (if granted) is already visible here. the intent is
    // spent either way — a cancelled sign-in must not pop this dialog later.
    useEffect(() => {
        if (!reopenPending || reauthenticationOpen) return;
        clearReopen();
        if (hasOwnerAuthority && !open) reopenStore();
    }, [
        reopenPending,
        reauthenticationOpen,
        hasOwnerAuthority,
        open,
        clearReopen,
        reopenStore,
    ]);

    return (
        <Dialog open={open} onOpenChange={(next) => !next && close()}>
            <DialogContent className="max-h-[85dvh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Passkeys</DialogTitle>
                    <DialogDescription>
                        Add up to 10 synced or device-bound passkeys. The
                        connected owner wallet approves every addition and
                        removal on Cronos.
                    </DialogDescription>
                </DialogHeader>

                {!hasOwnerAuthority && (
                    <div className="grid gap-3 rounded-xl border border-border bg-secondary/20 p-4">
                        <p className="text-sm text-muted-foreground">
                            Passkey management requires a connected wallet
                            because it sends a Cronos transaction from your
                            wallet.
                        </p>
                        <Button
                            type="button"
                            disabled={sessionLoading}
                            onClick={openConnectedWalletWindow}
                        >
                            {sessionLoading && (
                                <Loader2 className="animate-spin" />
                            )}
                            Open Connected Wallet window
                        </Button>
                    </div>
                )}

                {hasOwnerAuthority && (
                    <div className="flex flex-col gap-2">
                        {credentials.map((credential) => (
                            <div
                                key={credential.id}
                                className="flex items-center gap-3 rounded-xl border border-border p-3"
                            >
                                <Fingerprint className="size-5 text-primary" />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold">
                                        {credential.label || 'Passkey'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {statusLabel(credential)}
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon-sm"
                                    aria-label={`Remove ${credential.label || 'passkey'}`}
                                    disabled={busyId === credential.id}
                                    onClick={() =>
                                        void handleRemove(credential)
                                    }
                                >
                                    {busyId === credential.id ? (
                                        <Loader2 className="animate-spin" />
                                    ) : (
                                        <Trash2 />
                                    )}
                                </Button>
                            </div>
                        ))}
                        {!isLoading && credentials.length === 0 && (
                            <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
                                No passkeys enrolled yet.
                            </p>
                        )}
                    </div>
                )}

                {hasOwnerAuthority && (
                    <div className="grid gap-3 rounded-xl border border-border bg-secondary/20 p-3">
                        <label className="grid gap-1 text-sm font-medium">
                            Passkey label
                            <input
                                aria-label="Passkey label"
                                value={label}
                                maxLength={64}
                                placeholder="Phone, laptop…"
                                onChange={(event) => {
                                    setLabelEdited(true);
                                    setLabel(event.target.value);
                                }}
                                className="h-9 rounded-lg border border-border bg-background px-3"
                            />
                        </label>
                        <Button
                            type="button"
                            onClick={() => void handleAdd()}
                            disabled={
                                isLoading || credentials.length >= MAX_PASSKEYS
                            }
                        >
                            {isLoading ? (
                                <Loader2 className="animate-spin" />
                            ) : (
                                <KeyRound />
                            )}
                            {phase ? PHASE_BUTTON[phase] : 'Add passkey'}
                        </Button>
                        {phase && (
                            <p
                                role="status"
                                className="text-xs leading-relaxed text-muted-foreground"
                            >
                                {PHASE_DETAIL[phase]}
                            </p>
                        )}
                        {credentials.length >= MAX_PASSKEYS && (
                            <p className="text-xs text-muted-foreground">
                                You can enroll up to 10 passkeys. Remove one
                                before adding another.
                            </p>
                        )}
                    </div>
                )}

                {error && (
                    <p role="alert" className="text-sm text-destructive">
                        {error}
                    </p>
                )}

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={close}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PasskeyManagementDialog;
